import { ClientService, ToastMessageService } from '@fitness-force/api';
import { AsyncPipe, NgClass } from '@angular/common';

import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  input,
  output,
  inject,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import {
  AppErrors,
  BadRequestError,
  NotFoundError,
} from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { BlockUIModule } from 'primeng/blockui';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';

import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';

import { BehaviorSubject, Subscription, Subject, of } from 'rxjs';
import type {
  TENANTSINTERFACE,
  UpdateClientRequest,
} from '@fitness-force/models';
import { Store } from '@ngrx/store';

@Component({
  selector: 'assign-key-fob',
  templateUrl: './assign-key-fob.component.html',
  styleUrls: ['./assign-key-fob.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SelectModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    AvatarModule,
    ButtonModule,
    RippleModule,
    BlockUIModule,
    StyleClassModule,
    InputTextModule,
    ErrorMsgComponent,
    TranslateModule,
    AsyncPipe,
    NgClass,
  ],
})
export class AssignKeyFobComponent implements OnInit {
  private fb = inject(UntypedFormBuilder);
  translate = inject(TranslateService);
  private clientService = inject(ClientService);
  private toastMessageService = inject(ToastMessageService);
  private store = inject(Store);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private toastService = inject(ToastMessageService);

  readonly assignKeyFobModal = input<boolean | null>(false);
  readonly clientData$ = input<BehaviorSubject<any>>(
    new BehaviorSubject<any>(null),
  );
  readonly clientId = input<number>(0);
  readonly closeassignKeyFobModalDialog = output<any>();
  assignKeyFobFormGroup: UntypedFormGroup = new UntypedFormGroup({});
  //validAlphaNumericPattern = "^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+)$";
  validAlphaNumericPattern = '^[a-zA-Z0-9]*$';
  allClientData: any = null;
  tenantId: number = 0;
  btnLoading: boolean = false;
  isValidatingKeyfob: boolean = false;
  isKeyfobValid: boolean = false;
  keyfobValidationMessage: string = '';

  constructor() {}

  ngOnInit(): void {
    this.componentInit();
  }

  componentInit() {
    // Get tenant info from store state
    this.store
      .select((state: any) => state.tenants)
      .subscribe((tenant: TENANTSINTERFACE) => {
        if (tenant && tenant.selected_tenant) {
          this.tenantId = tenant.selected_tenant.id;
          if (this.tenantId > 0) {
            const clientDataSubject = this.clientData$();
            if (clientDataSubject) {
              clientDataSubject.subscribe((res: any) => {
                this.allClientData = res;
                this.assignKeyFobFormGroup = this.fb.group({
                  keyFobNumber: [
                    res?.current_keyfob ? res?.current_keyfob : '',
                    [
                      Validators.required,
                      Validators.maxLength(12),
                      Validators.pattern(this.validAlphaNumericPattern),
                    ],
                  ],
                });
              });
            }

            const clientId = this.clientId();
            if (clientId && clientId > 0) {
              this.clientService.getClientById(clientId).subscribe({
                next: (res: any) => {
                  this.clientData$().next(res);
                },
                error: () => {
                  // Handle error silently
                },
              });
            }
          }
        }
      });
  }

  defaultAssignKeyFobFormValue() {
    this.keyFobNumber.setValue(this.allClientData.mobile_number);
  }

  get keyFobNumber(): UntypedFormControl {
    return this.assignKeyFobFormGroup.controls
      .keyFobNumber as UntypedFormControl;
  }

  closeDialogEvent() {
    this.closeassignKeyFobModalDialog.emit(false);
  }

  deleteKeyFob() {
    if (!this.allClientData || !this.allClientData.id) {
      this.toastService.showErrorMessage('Client data is not available');
      return;
    }

    this.btnLoading = true;
    const body: Array<{ op: string; path: string; value: any }> = [];
    body.push({ op: 'replace', path: '/CurrentKeyfob', value: '' });

    this.clientService
      .patchClient(this.allClientData.id, this.tenantId, body)
      .subscribe({
        next: async (res) => {
          this.btnLoading = false;
          this.translate
            .get('CLIENT_PROFILE.KYE_FOB.DELETE_SUCCESS_MSG')
            .subscribe((translation) => {
              this.toastService.showSuccess(translation);
            });
          await this.timeout(1000);
          this.btnLoading = false;
          this.closeassignKeyFobModalDialog.emit({ action: 'updated' });
        },
        error: (error) => {
          this.btnLoading = false;
          this.handleError(error);
        },
      });
  }

  postAssignKeyFob() {
    // Check if client data is available
    if (!this.allClientData || !this.allClientData.id) {
      this.toastService.showErrorMessage('Client data is not available');
      return;
    }

    // Check if form is valid
    if (!this.assignKeyFobFormGroup.valid) {
      Object.keys(this.assignKeyFobFormGroup.controls).forEach((field) => {
        const control = this.assignKeyFobFormGroup.get(field);
        control?.markAsDirty({ onlySelf: true });
      });
      return;
    }

    const currentValue = this.keyFobNumber.value?.trim() || '';
    const currentKeyfob = this.allClientData?.current_keyfob || '';
    const isKeyfobChanged = currentValue !== currentKeyfob;

    // If keyfob hasn't changed, proceed directly with assignment
    if (!isKeyfobChanged) {
      this.assignKeyFob(currentValue);
      return;
    }

    // Validate keyfob before assigning
    this.isValidatingKeyfob = true;
    this.keyfobValidationMessage = '';
    this.changeDetectorRef.detectChanges();

    this.clientService
      .validateKeyfob(this.tenantId, currentValue, this.allClientData.id)
      .subscribe({
        next: (response) => {
          this.isValidatingKeyfob = false;
          if (response.isValid) {
            this.isKeyfobValid = true;
            this.keyfobValidationMessage = '';
            this.assignKeyFob(currentValue);
          } else {
            this.isKeyfobValid = false;
            this.keyfobValidationMessage = 'Invalid keyfob';
            this.changeDetectorRef.detectChanges();
          }
        },
        error: (error) => {
          this.isValidatingKeyfob = false;
          this.isKeyfobValid = false;
          if (error?.originalError?.error?.errorMessage) {
            this.keyfobValidationMessage =
              error.originalError.error.errorMessage;
          } else {
            this.keyfobValidationMessage = 'Invalid keyfob number';
          }
          this.changeDetectorRef.detectChanges();
        },
      });
  }

  assignKeyFob(keyfobValue: string) {
    if (!this.allClientData || !this.allClientData.id) {
      this.toastService.showErrorMessage('Client data is not available');
      return;
    }

    this.btnLoading = true;
    const body: Array<{ op: string; path: string; value: any }> = [];
    body.push({ op: 'replace', path: '/CurrentKeyfob', value: keyfobValue });

    this.clientService
      .patchClient(this.allClientData.id, this.tenantId, body)
      .subscribe({
        next: async (res) => {
          this.btnLoading = false;
          this.translate
            .get('CLIENT_PROFILE.KYE_FOB.SUCCESS_MSG')
            .subscribe((translation) => {
              this.toastService.showSuccess(translation);
            });
          await this.timeout(1000);
          this.btnLoading = false;
          this.closeassignKeyFobModalDialog.emit({ action: 'updated' });
        },
        error: (error) => {
          this.btnLoading = false;
          this.handleError(error);
        },
      });
  }

  get canSubmitForm(): boolean {
    const currentValue = this.keyFobNumber.value?.trim() || '';
    return currentValue.length >= 3;
  }

  handleError(error: any) {
    if (error?.originalError?.error?.length > 0) {
      error.originalError.error.forEach((e: any) => {
        if (
          e.errorCode == 'alreadyexist' &&
          e.identifier == 'client_current_keyfob'
        ) {
          this.keyFobNumber.setErrors({
            duplicate: true,
          });
          this.toastService.showErrorMessage(e.errorMessage);
          this.changeDetectorRef.detectChanges();
        } else {
          this.toastService.showErrorMessage(e.errorMessage);
          this.changeDetectorRef.detectChanges();
        }
      });
    } else if (error?.originalError?.error) {
      if (
        error.originalError.error.errorCode == 'alreadyexist' &&
        error.originalError.error.identifier == 'client_current_keyfob'
      ) {
        this.keyFobNumber.setErrors({
          duplicate: true,
        });
        this.toastService.showErrorMessage(
          error.originalError.error.errorMessage,
        );
        this.changeDetectorRef.detectChanges();
      } else {
        this.toastService.showErrorMessage(
          error.originalError.error.errorMessage || 'An error occurred',
        );
      }
      this.changeDetectorRef.detectChanges();
    } else {
      this.toastService.showErrorMessage('An error occurred');
      this.changeDetectorRef.detectChanges();
    }
  }

  timeout(ms: any) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
